import type React from 'react';
import { Box, Checkbox, Stack, Typography } from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { radius } from '@karehero/llama/theme/radius';

/**
 * The Attendance Allowance prototype's copy of
 * `components/wrapped/ChatActionRow`,
 * so Vez's shared component, and every screen that uses it, stays exactly as
 * she built it.
 *
 * What differs: the row is a button, or a checkbox for a pick-several, so it
 * is reached by Tab, pressed with Enter or Space, and shows a focus ring.
 *
 * These are proposals for the shared component: fold them back, or delete
 * this copy, once she has looked.
 */

/**
 * A tappable row inside a chat: an offer, or the way on.
 *
 * An offer is not a message. On floral white with a neutral border it was the
 * same surface as a bubble and the same surface as a card, so a row you could
 * tap sat in the transcript looking like something that had been said. The
 * lightest marine with a step darker around it reads as a control at a glance
 * and still sits quietly under the question.
 *
 * `go` is the last thing in a turn, the way on rather than a thing on offer, and
 * it takes lime instead.
 *
 * `selectable` puts a checkbox on the row, for a question that takes more than
 * one answer. Without it a pick-several looks exactly like a pick-one: the rows
 * are identical, so the only thing saying a second answer is allowed is the
 * "Continue when you're done" underneath them, which is read after the choice
 * has already been made.
 *
 * Wrapped, not a variant: it carries the tone and the icon colour together, and
 * it is the one object every chat in the app should use for this. Lifted out of
 * ChatV2, where it started, so the intake and the pre-sign-up screens cannot
 * drift into two teals.
 */
const ClaimActionRow = ({
  label,
  icon,
  tone = 'offer',
  selectable = false,
  selected = false,
  onClick,
}: {
  label: string;
  icon?: string;
  tone?: 'offer' | 'go';
  /** Draw a checkbox, for a row belonging to a question that takes several. */
  selectable?: boolean;
  selected?: boolean;
  onClick?: () => void;
}) => {
  const go = tone === 'go';

  return (
    <Stack
      onClick={onClick}
      // A row is a button, or a checkbox where the question takes several:
      // reachable by Tab, pressed with Enter or Space, and announced as one.
      role={selectable ? 'checkbox' : 'button'}
      aria-checked={selectable ? selected : undefined}
      tabIndex={0}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
      sx={{
        alignItems: 'center',
        '&:focus-visible': {
          outline: '3px solid',
          outlineColor: 'primary.main',
          outlineOffset: 2,
        },
        px: 2,
        py: 1.25,
        bgcolor: go ? 'custom.limeGreen.200' : 'custom.marineGreen.50',
        border: 1,
        borderColor: go ? 'primary.main' : 'custom.marineGreen.100',
        borderRadius: `${radius.md}px`,
        cursor: 'pointer',
        // Tapping one of these is how a closed question gets answered, so it has
        // to feel like a button being pressed rather than a message being
        // touched.
        //
        // The press is the state that matters, because on a phone there is no
        // hover: the border goes marine and the row gives a per cent under the
        // thumb.
        //
        // The fill stops at one step darker for both. Two steps down the ramp
        // is a mid marine, and the label is charcoal: dark text on a dark fill
        // reads as a row going out rather than a row being pressed. So the
        // border and the squeeze carry the press, which is also all the lime
        // row has, its ramp being flat.
        transition:
          'background-color 120ms ease, border-color 120ms ease, transform 120ms ease',
        '&:hover': {
          bgcolor: go ? 'custom.limeGreen.200' : 'custom.marineGreen.100',
          borderColor: go ? 'primary.dark' : 'custom.marineGreen.200',
        },
        '&:active': {
          bgcolor: go ? 'custom.limeGreen.200' : 'custom.marineGreen.100',
          borderColor: 'primary.main',
          transform: 'scale(0.99)',
        },
      }}
      direction='row'
      spacing={1.5}
    >
      {/* Marine, not coral. Coral at step 500 on the lightest marine is barely
          there: the row went teal and the icon stayed the colour it had been
          when the row was floral white.

          Absent where the row is an answer: a tick or a camera says what
          tapping does, and "Yes, I'm helping look after someone" already
          does. */}
      {selectable ? (
        <Checkbox
          checked={selected}
          color='primary'
          tabIndex={-1}
          aria-hidden
          sx={{ p: 0 }}
        />
      ) : (
        icon && (
          <Box sx={{ color: 'primary.main', display: 'flex' }}>
            <Icon icon={icon as never} fontSize={16} />
          </Box>
        )
      )}
      <Typography variant='body1'>{label}</Typography>
    </Stack>
  );
};

ClaimActionRow.displayName = 'ClaimActionRow';
export default ClaimActionRow;
